import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Card,
  FloatingSave,
  NumberInput,
  Page,
  SimpleSelect,
  SwitchInput,
  TextInput,
  UrlInput,
} from 'corso-admin-ui';
import {
  claimWindowStartEventName,
  CrewIncentiveTypeEnum,
  isTruthy,
  Pegasus,
  storeInventoryBehaviorName,
} from 'corso-types';
import { FormEventHandler, useRef } from 'react';
import {
  Controller,
  FormProvider,
  useForm,
  useFormContext,
} from 'react-hook-form';
import MonetaryRateInput from '~/components/field/MonetaryRateInput';
import { AutomationListByEventType } from '~/features/settings/automations/AutomationListByEventType';
import GiftCardTypeSelect from '~/features/settings/components/GiftCardTypeSelect';
import SettingsAnchorCard from '~/features/settings/components/SettingsAnchorCard';
import { useCurrency } from '~/hooks/useCurrency';
import {
  useReturnSettings,
  useReturnSettingsUpdate,
} from '~/hooks/useReturnSettings';
import { returnSettingsFormSchema, UseFormTypes } from '~/types';
import { getNullableNumberValue } from '~/utils/form';
import ShopNow from './ShopNow';

function useReturnSettingsForm() {
  const { data } = useReturnSettings();
  return useForm({
    resolver: zodResolver(returnSettingsFormSchema),
    values: data,
  });
}

export function useReturnSettingsFormContext() {
  type FormType = UseFormTypes<typeof useReturnSettingsForm>;
  return useFormContext<
    FormType['FieldValues'],
    FormType['Context'],
    FormType['TransformedValues']
  >();
}

function ProductExchanges() {
  const { watch } = useReturnSettingsFormContext();

  const isEnabled = watch('variantExchanges.isExchangeEnabled');

  if (!isEnabled) return null;

  return (
    <SettingsAnchorCard id="product-exchanges">
      <AutomationListByEventType
        eventType={Pegasus.EventType.offerAdvancedExchange}
        hideIcon
      />
    </SettingsAnchorCard>
  );
}

function ReturnsGeneralCard() {
  const {
    register,
    formState: { errors },
    control,
  } = useReturnSettingsFormContext();

  return (
    <SettingsAnchorCard id="general">
      <UrlInput
        id="return-policy-url"
        label="Return Policy URL"
        placeholder="https://company.com/returns-policy"
        {...register('returns.returnPolicyUrl')}
        error={errors.returns?.returnPolicyUrl?.message}
      />

      <Controller
        control={control}
        name="returns.isClaimImageFromCustomerRequired"
        defaultValue={false}
        render={({ field: { onChange, value }, fieldState }) => (
          <SwitchInput
            id="claim-image-from-customer-required"
            label="Require Media Upload"
            details="Enforce that customers provide an image or video when submitting a return request."
            checked={value}
            onChange={onChange}
            error={fieldState.error?.message}
          />
        )}
      />
      <Controller
        control={control}
        name="returns.isReturnInspectionEnabled"
        defaultValue={false}
        render={({ field: { onChange, value }, fieldState }) => (
          <SwitchInput
            id="return-inspection-enabled"
            label="Inspections"
            details="Ability to add an inspection to a return request."
            checked={value}
            onChange={onChange}
            error={fieldState.error?.message}
          />
        )}
      />

      <NumberInput
        id="return-expiration-days"
        label="Return Expiration Days"
        details="The number of days after the date the return was created that a customer can access the label or packing slip to return the item. "
        addon={{
          insideEnd: 'days',
        }}
        required
        {...register('returns.defaultReturnExpirationDays', {
          valueAsNumber: true,
        })}
        error={errors.returns?.defaultReturnExpirationDays?.message}
      />

      <Controller
        control={control}
        name="returns.isGiftReturnEnabled"
        defaultValue={false}
        render={({ field: { onChange, value }, fieldState }) => (
          <SwitchInput
            id="gift-return-enabled"
            label="Allow Gift Returns"
            details="Enables customers to return items that were received as gifts. Gift returns are excluded from refunds and do not notify the purchaser."
            checked={value}
            onChange={onChange}
            error={fieldState.error?.message}
          />
        )}
      />
    </SettingsAnchorCard>
  );
}

function ReturnsECommerceCard() {
  const {
    control,
    register,
    formState: { errors },
    watch,
  } = useReturnSettingsFormContext();
  const returnOrderPrefix = watch('returns.returnOrderPrefix');
  const exchangeOrderPrefix = watch('returns.exchangeOrderPrefix');
  const giftCardOrderPrefix = watch('giftCards.giftCardOrderPrefix');

  const isExchangeEnabled = watch('variantExchanges.isExchangeEnabled');
  const isReturnProcessEnabled = watch('returns.isReturnProcessApiEnabled');
  const isGiftCardEnabled = watch('giftCardsControl.isGiftCardEnabled');
  const giftCardType = watch('giftCards.giftCardType');

  const windowStartOptions = Object.entries(claimWindowStartEventName).map(
    ([value, label]) => ({
      label,
      value,
    }),
  );

  const inventoryBehaviorOptions = Object.entries(
    storeInventoryBehaviorName,
  ).map(([value, label]) => ({
    label,
    value,
  }));

  return (
    <Card>
      <Card.Heading as="h2">E-Commerce Platform</Card.Heading>

      <Controller
        control={control}
        name="returns.allowClaimsOnPartiallyFulfilledOrders"
        defaultValue={false}
        render={({ field: { onChange, value }, fieldState }) => (
          <SwitchInput
            id="allow-claims-on-partially-fulfilled-orders"
            label="Allow Claims on Partially Fulfilled Orders"
            details="When enabled, customers can create claims on orders that have not been fully fulfilled."
            checked={value}
            onChange={onChange}
            error={fieldState.error?.message}
            disabled={isReturnProcessEnabled}
          />
        )}
      />

      <Controller
        control={control}
        name="returns.claimWindowStartEvent"
        render={({ field: { onChange, value }, fieldState }) => (
          <SimpleSelect
            onChange={onChange}
            options={windowStartOptions}
            value={value}
            label="Return Window Start Date"
            error={fieldState.error?.message}
            details="The date to use when determining the start of the return window."
          />
        )}
      />

      <Controller
        control={control}
        name="returns.inventoryBehavior"
        render={({ field: { onChange, value }, fieldState }) => (
          <SimpleSelect
            onChange={onChange}
            options={inventoryBehaviorOptions}
            value={value}
            label="Inventory Behavior"
            error={fieldState.error?.message}
            details="The default Shopify inventory behavior to apply when finalizing a claim that results in a new order. Can be overridden per-automation in Configure Out Of Stock."
          />
        )}
      />

      {isExchangeEnabled && !isReturnProcessEnabled && (
        <Controller
          control={control}
          name="returns.shouldConsolidateOrdersAtFinalization"
          defaultValue={false}
          render={({ field: { onChange, value }, fieldState }) => (
            <SwitchInput
              id="should-consolidate-orders-at-finalization"
              label="Consolidate Exchange and Replacement Orders"
              details="When enabled, consolidate replacement orders and variant exchanges into a single order on Shopify, using the Replacement Order Prefix and Replacement Order Discount Code."
              checked={value}
              onChange={onChange}
              error={fieldState.error?.message}
            />
          )}
        />
      )}

      <TextInput
        id="replacement-order-prefix"
        label="Replacement Order Prefix"
        details={
          returnOrderPrefix
            ? `The prefix to add to replacement orders, e.g. ${returnOrderPrefix}1234`
            : 'The prefix to add to replacement orders.'
        }
        placeholder="#"
        {...register('returns.returnOrderPrefix')}
        error={errors.returns?.returnOrderPrefix?.message}
      />
      <TextInput
        id="replacement-order-discount-code"
        label="Replacement Order Discount Code"
        details="The discount code used to do zero cost replacement orders, including Shop Now."
        {...register('returns.replacementOrderDiscountCode')}
        error={errors.returns?.replacementOrderDiscountCode?.message}
      />

      {isGiftCardEnabled && giftCardType === 'Gift_Card_Order' && (
        <TextInput
          id="gift-card-order-prefix"
          label="Store Credit Order Prefix"
          details={
            giftCardOrderPrefix
              ? `The prefix to add to gift card orders, e.g. ${giftCardOrderPrefix}1234`
              : 'The prefix to add to gift card orders.'
          }
          placeholder="#"
          {...register('giftCards.giftCardOrderPrefix')}
          error={errors.giftCards?.giftCardOrderPrefix?.message}
        />
      )}

      {isExchangeEnabled && !isReturnProcessEnabled && (
        <>
          <TextInput
            id="exchange-order-prefix"
            label="Exchange Order Prefix"
            details={
              exchangeOrderPrefix
                ? `The prefix to add to exchange orders, e.g. ${exchangeOrderPrefix}1234`
                : 'The prefix to add to exchange orders.'
            }
            placeholder="#"
            {...register('returns.exchangeOrderPrefix')}
            error={errors.returns?.exchangeOrderPrefix?.message}
          />
          <TextInput
            id="exchange-order-discount-code"
            label="Exchange Order Discount Code"
            details="The discount code used to do zero cost variant exchanges."
            {...register('returns.exchangeOrderDiscountCode')}
            error={errors.returns?.exchangeOrderDiscountCode?.message}
          />
        </>
      )}
    </Card>
  );
}

function RefundsCard() {
  const {
    register,
    formState: { errors },
    control,
    watch,
    setValue,
  } = useReturnSettingsFormContext();
  const isEnabled = watch('refunds.isRefundsEnabled');

  return (
    <SettingsAnchorCard id="refunds">
      <div className="flex items-center justify-between gap-2">
        <Card.Heading as="h2">Refunds</Card.Heading>
        <Controller
          control={control}
          name="refunds.isRefundsEnabled"
          defaultValue={false}
          render={({ field: { onChange, value }, fieldState }) => (
            <SwitchInput
              id="refunds-offered"
              label="Refunds Offered"
              checked={value}
              onChange={(e) => {
                const { checked } = e.target;
                setValue('refunds.refundValidityDays', checked ? 30 : 0, {
                  shouldDirty: true,
                });
                onChange(e);
              }}
              error={fieldState.error?.message}
            />
          )}
        />
      </div>
      {isEnabled && (
        <NumberInput
          id="refund-validity-days"
          label="Refund Window"
          details="The number of days from the date an order was fulfilled that a customer can request a refund."
          addon={{
            insideEnd: 'days',
          }}
          required
          {...register('refunds.refundValidityDays', {
            valueAsNumber: true,
          })}
          error={errors.refunds?.refundValidityDays?.message}
        />
      )}
    </SettingsAnchorCard>
  );
}

function GiftCardsCard() {
  const { currencyCode, currencySymbol } = useCurrency();
  const {
    register,
    formState: { errors },
    control,
    watch,
    setValue,
  } = useReturnSettingsFormContext();
  const isEnabled = watch('giftCardsControl.isGiftCardEnabled');

  const incentiveType = watch('giftCards.giftCardIncentiveType');
  const incentiveAmount = watch('giftCards.giftCardIncentiveAmount');
  const isReturnProcessEnabled = watch('returns.isReturnProcessApiEnabled');

  const giftCardType = watch('giftCards.giftCardType');

  const giftCardExpirationDays = watch('giftCards.giftCardExpirationDays');
  return (
    <SettingsAnchorCard id="store-credit">
      <div className="flex items-center justify-between gap-2">
        <Card.Heading as="h2">Store Credit</Card.Heading>
        <Controller
          control={control}
          name="giftCardsControl.isGiftCardEnabled"
          defaultValue={false}
          render={({ field: { onChange, value }, fieldState }) => (
            <SwitchInput
              id="store-credit-offered"
              label="Store Credit Offered"
              checked={value}
              onChange={(e) => {
                const { checked } = e.target;
                setValue(
                  'giftCardsControl.giftCardValidityDays',
                  checked ? 30 : 0,
                  { shouldDirty: true },
                );
                onChange(e);
              }}
              error={fieldState.error?.message}
            />
          )}
        />
      </div>
      {isEnabled && (
        <>
          <NumberInput
            id="gift-card-validity-days"
            label="Store Credit Window"
            details="The number of days from the date an order was fulfilled that a customer can request store credit."
            addon={{
              insideEnd: 'days',
            }}
            required
            {...register('giftCardsControl.giftCardValidityDays', {
              valueAsNumber: true,
            })}
            error={errors.giftCardsControl?.giftCardValidityDays?.message}
          />

          <MonetaryRateInput
            id="gift-card-incentive-amount"
            label="Store Credit Incentive Amount"
            details="The amount that will be added to the store credit amount when a customer requests store credit."
            required
            currencyCode={currencyCode}
            currencySymbol={currencySymbol}
            value={{
              type:
                incentiveType !== CrewIncentiveTypeEnum.none
                  ? incentiveType
                  : CrewIncentiveTypeEnum.fixed,
              amount: incentiveAmount,
            }}
            onChange={({ type, amount }) => {
              setValue('giftCards.giftCardIncentiveType', type, {
                shouldDirty: true,
                shouldValidate: true,
                shouldTouch: true,
              });
              setValue('giftCards.giftCardIncentiveAmount', amount, {
                shouldDirty: true,
                shouldValidate: true,
                shouldTouch: true,
              });
            }}
            error={errors.giftCards?.giftCardIncentiveAmount?.message}
          />

          <Controller
            control={control}
            name="giftCards.giftCardType"
            render={({ field, fieldState }) => (
              <GiftCardTypeSelect
                allowStoreCredit={isReturnProcessEnabled}
                value={field.value}
                onChange={(value) => field.onChange(value)}
                error={fieldState.error?.message}
              />
            )}
          />

          {giftCardType === 'Gift_Card' && (
            <>
              <TextInput
                id="gift-card-template-suffix"
                label="Gift Card Template Suffix"
                details="Suffix of the liquid template used to render the gift card email, if none is provided the default gift_card.liquid template will be used."
                {...register('giftCards.giftCardAttributes.templateSuffix')}
                error={
                  errors.giftCards?.giftCardAttributes?.templateSuffix?.message
                }
              />
              <Controller
                control={control}
                name="giftCards.giftCardExpirationDays"
                render={({ field, fieldState }) => (
                  <NumberInput
                    id="gift-card-expiration-days"
                    label="Gift Card Expiration Days"
                    details={
                      giftCardExpirationDays
                        ? `The number of days after the gift card is created that it will expire.`
                        : 'The gift card will not expire.'
                    }
                    addon={{
                      insideEnd: 'days',
                    }}
                    onChange={(event) =>
                      field.onChange(getNullableNumberValue(event))
                    }
                    error={fieldState.error?.message}
                  />
                )}
              />
            </>
          )}
          {giftCardType === 'Gift_Card_Order' && (
            <TextInput
              id="gift-card-product-id-from-platform"
              label="Store Credit Gift Card Product ID"
              details="The product ID of the gift card product that will be added to the order when a customer requests store credit."
              {...register('giftCards.giftCardProductIdFromPlatform')}
              error={errors.giftCards?.giftCardProductIdFromPlatform?.message}
            />
          )}
        </>
      )}
    </SettingsAnchorCard>
  );
}

function VariantExchangesCard() {
  const {
    control,
    formState: { errors },
    register,
    setValue,
    watch,
  } = useReturnSettingsFormContext();

  const isEnabled = watch('variantExchanges.isExchangeEnabled');
  const isExchangeOfExchangeAllowed = watch(
    'variantExchanges.isExchangeOfExchangeAllowed',
  );
  const isClaimLineageEnabled = watch('variantExchanges.isClaimLineageEnabled');

  const isReturnProcessEnabled = watch('returns.isReturnProcessApiEnabled');

  return (
    <SettingsAnchorCard id="exchanges">
      <div className="flex items-center justify-between gap-2">
        <Card.Heading as="h2">Exchanges</Card.Heading>
        <Controller
          control={control}
          name="variantExchanges.isExchangeEnabled"
          defaultValue={false}
          render={({ field: { onChange, value }, fieldState }) => (
            <SwitchInput
              id="variant-exchanges-offered"
              label="Exchanges Offered"
              checked={value}
              onChange={(e) => {
                const { checked } = e.target;
                setValue(
                  'variantExchanges.variantExchangeValidityDays',
                  checked ? 30 : 0,
                  { shouldDirty: true },
                );
                onChange(e);
              }}
              error={fieldState.error?.message}
            />
          )}
        />
      </div>
      {/* applies to returns on existing exchange orders even with exchanges off */}
      <Controller
        control={control}
        name="variantExchanges.isClaimLineageEnabled"
        defaultValue={false}
        render={({ field: { onChange, value }, fieldState }) => (
          <SwitchInput
            id="claim-lineage-enabled"
            label="Claim Lineage"
            details="When enabled, claims on exchange orders follow your normal refund, store credit and exchange rules, and are valued and refunded against the original order."
            checked={value}
            onChange={onChange}
            error={fieldState.error?.message}
          />
        )}
      />
      {isEnabled && (
        <>
          <NumberInput
            id="variant-exchange-validity-days"
            label="Exchange Window"
            details="The number of days from the date an order was fulfilled that a customer can request an exchange."
            addon={{
              insideEnd: 'days',
            }}
            required
            {...register('variantExchanges.variantExchangeValidityDays', {
              valueAsNumber: true,
            })}
            error={
              errors.variantExchanges?.variantExchangeValidityDays?.message
            }
          />

          {!isClaimLineageEnabled && (
            <Controller
              control={control}
              name="variantExchanges.isExchangeOfExchangeAllowed"
              defaultValue={false}
              render={({ field: { onChange, value }, fieldState }) => (
                <SwitchInput
                  id="exchange-of-exchange-allowed"
                  label="Allow Exchange of Exchange"
                  details="When enabled, customers can create an exchange from a previously created exchange. The eligibility window will always be based on the original order."
                  checked={value}
                  onChange={onChange}
                  error={fieldState.error?.message}
                />
              )}
            />
          )}
          {!isClaimLineageEnabled && isExchangeOfExchangeAllowed && (
            <Controller
              control={control}
              name="variantExchanges.isExchangeOfExchangeCreditEnabled"
              defaultValue={false}
              render={({ field: { onChange, value }, fieldState }) => (
                <SwitchInput
                  id="exchange-of-exchange-credit-enabled"
                  label="Offer Credit for Exchange of Exchange"
                  details="When enabled, allow a store credit to be offered for exchange of exchange orders."
                  checked={value}
                  onChange={onChange}
                  error={fieldState.error?.message}
                />
              )}
            />
          )}

          {!isReturnProcessEnabled && (
            <TextInput
              id="exchange-order-shipping-title"
              label="Shipping Title"
              details="The shipping title to use for exchange orders."
              {...register('variantExchanges.variantExchangeShippingTitle')}
              error={
                errors.variantExchanges?.variantExchangeShippingTitle?.message
              }
            />
          )}

          <NumberInput
            id="min-inventory-available-to-customer"
            label="Minimum Inventory Available to Customer"
            details="The minimum inventory available for a variant to be eligible for exchange; otherwise, the variant will be considered out of stock."
            required
            {...register('variantExchanges.minInventoryAvailableToCustomer', {
              valueAsNumber: true,
            })}
            error={
              errors.variantExchanges?.minInventoryAvailableToCustomer?.message
            }
          />
        </>
      )}
    </SettingsAnchorCard>
  );
}

export default function ReturnsSettingsPage() {
  const formId = 'returns-settings';
  const formRef = useRef<HTMLFormElement>(null);

  const { mutateAsync: saveConfigChanges } = useReturnSettingsUpdate();

  const methods = useReturnSettingsForm();
  const submitHandler: FormEventHandler = (event) => {
    methods
      .handleSubmit((values) => saveConfigChanges(values))(event)
      .catch(console.error);
  };

  const isReturnsEnabled = methods.watch('returns.isReturnsEnabled');

  return (
    <FormProvider {...methods}>
      <Page
        title="Returns"
        secondaryActions={[
          isReturnsEnabled && {
            id: 'disable-returns',
            content: 'Turn Off',
            onAction: () => {
              methods.setValue('returns.isReturnsEnabled', false, {
                shouldDirty: true,
              });
              formRef.current?.requestSubmit();
            },
          },
        ].filter(isTruthy)}
        primaryAction={
          isReturnsEnabled
            ? undefined
            : {
                onAction: () => {
                  methods.setValue('returns.isReturnsEnabled', true, {
                    shouldDirty: true,
                  });
                  formRef.current?.requestSubmit();
                },
                content: 'Turn On',
              }
        }
      >
        <form
          id={formId}
          ref={formRef}
          className="flex flex-col gap-4"
          onSubmit={submitHandler}
          onReset={(e) => {
            e.preventDefault();
            methods.reset();
          }}
        >
          {!isReturnsEnabled && (
            <Alert
              elevated
              variant="info"
              heading="Returns are Disabled. Settings can be configured, but returns will not be offered to customers."
            />
          )}

          <FloatingSave
            isDirty={methods.formState.isDirty}
            isSubmitting={methods.formState.isSubmitting}
            form={formId}
          />

          <ReturnsGeneralCard />
          <ReturnsECommerceCard />
          <RefundsCard />
          <GiftCardsCard />
          <VariantExchangesCard />
          <ShopNow />
          <ProductExchanges />
        </form>
      </Page>
    </FormProvider>
  );
}
